import { useCallback, useMemo, useState } from 'react'

import type { LeaveRequestFormState } from '@/features/leave/components/LeaveRequestForm'
import type {
  LeaveTypeDto,
  MyEligibleLeaveTypesParams,
  MyRequestsParams,
} from '@/utils/api/leave-service/models'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { LeaveEntitlementsTab } from '@/features/leave/components/LeaveEntitlementsTab'
import { LeaveRequestForm } from '@/features/leave/components/LeaveRequestForm'
import { handleError } from '@/lib/utils'
import { useMyEligibleLeaveTypes, useMyRequests } from '@/utils/api/leave-service'

import { LeaveApplicationsTab } from '../components/LeaveApplicationsTab'

export function MyLeaveManagementPage() {
  const [requestDialogOpen, setRequestDialogOpen] = useState(false)
  const [requestFormKey, setRequestFormKey] = useState(0)
  const [activeTab, setActiveTab] = useState<'applications' | 'entitlements'>('applications')
  const [preselectedLeaveTypeId, setPreselectedLeaveTypeId] = useState<string>('')

  const requestsParams = useMemo<MyRequestsParams>(() => ({
    pageable: { page: 0, size: 200 },
  }), [])
  const leaveTypesParams = useMemo<MyEligibleLeaveTypesParams>(() => ({
    pageable: { page: 0, size: 200 },
  }), [])

  const myRequestsQuery = useMyRequests(requestsParams)
  const myEligibleLeaveTypesQuery = useMyEligibleLeaveTypes(leaveTypesParams)

  const handleRequestDialogChange = useCallback((open: boolean) => {
    setRequestDialogOpen(open)
    if (!open) {
      setRequestFormKey(prev => prev + 1)
      setPreselectedLeaveTypeId('')
    }
  }, [])

  const { refetch: refetchMyRequests } = myRequestsQuery
  const handleRequestSubmitted = useCallback(() => {
    setRequestDialogOpen(false)
    setRequestFormKey(prev => prev + 1)
    void refetchMyRequests()
  }, [refetchMyRequests])

  const handleApplyLeaveFromEntitlements = useCallback((leaveType: LeaveTypeDto) => {
    setActiveTab('applications')
    setPreselectedLeaveTypeId(leaveType.id?.toString() ?? '')
    setRequestDialogOpen(true)
  }, [])

  const canRequestForLeave = (myEligibleLeaveTypesQuery.data?.data?.totalElements ?? 0) > 0
  const initialLeaveRequestState = useMemo<LeaveRequestFormState | undefined>(() => {
    if (!preselectedLeaveTypeId) {
      return undefined
    }
    return {
      leaveTypeId: preselectedLeaveTypeId,
      startDate: '',
      endDate: '',
      reason: '',
      requestedDays: '',
    }
  }, [preselectedLeaveTypeId])

  if (myEligibleLeaveTypesQuery.isError) {
    handleError(myEligibleLeaveTypesQuery.error || 'Failed to load eligible leave types. Please try again later.')
  }

  return (
    <div className="space-y-6">
      <Dialog open={requestDialogOpen} onOpenChange={handleRequestDialogChange}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request Leave</DialogTitle>
            <DialogDescription>Submit a new leave request for approval.</DialogDescription>
          </DialogHeader>
          <LeaveRequestForm
            key={requestFormKey}
            onSubmitted={handleRequestSubmitted}
            myEligibleLeaveTypesData={myEligibleLeaveTypesQuery.data?.data?.content ?? []}
            leaveTypesLoading={myEligibleLeaveTypesQuery.isPending}
            initialState={initialLeaveRequestState}
          />
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between pb-3">
          <div className="space-y-1">
            <CardTitle>Leave Management</CardTitle>
            <CardDescription>Review your leave applications and entitlements.</CardDescription>
          </div>
          {activeTab === 'applications' && (
            <div className={`flex flex-wrap items-center gap-2 ${!canRequestForLeave ? 'cursor-not-allowed opacity-50' : ''}`}>
              <Button
                type="button"
                onClick={() => {
                  setPreselectedLeaveTypeId('')
                  setRequestDialogOpen(true)
                }}
                disabled={!canRequestForLeave}
                title={canRequestForLeave ? undefined : 'You have no eligible leave types to request leave.'}
              >
                Request Leave
              </Button>
            </div>
          )}
        </CardHeader>
        <Tabs value={activeTab} onValueChange={value => setActiveTab(value as 'applications' | 'entitlements')} className="w-full">
          <TabsList className="px-4">
            <TabsTrigger value="applications">Leave Applications</TabsTrigger>
            <TabsTrigger value="entitlements">Leave Entitlements</TabsTrigger>
          </TabsList>
          <CardContent className="pt-6">
            <TabsContent value="applications" className="space-y-4">
              <LeaveApplicationsTab />
            </TabsContent>
            <TabsContent value="entitlements" className="space-y-4">
              <LeaveEntitlementsTab onApplyLeave={handleApplyLeaveFromEntitlements} />
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>
    </div>
  )
}
